import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuiz } from '../context/QuizContext';
import { determineResultType } from '../utils/quizLogic';
import { Button } from '../components/ui/Button';
import { rentAmountOptions, bestTimeOptions } from '../data/questions';
import supabase from '../supabase/supabase';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

export function LeadForm() {
  const { answers, submitLead } = useQuiz();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    zipCode: '',
    currentRent: '',
    bestTime: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const resultType = determineResultType(answers);

    try {
      // 1. Save to Supabase
      const { error: dbError } = await supabase
        .from('clients_20250522')
        .insert([
          {
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            zip: formData.zipCode,
            current_rent: formData.currentRent,
            best_time: formData.bestTime,
            quiz_result: resultType,
            source: 'renters_insurance_quiz', // Track the source as requested
            answers: answers
          }
        ]);

      if (dbError) throw dbError;

      // 2. Update local context
      submitLead(formData, resultType);

      // 3. Navigate to results
      navigate('/result');
    } catch (err) {
      console.error('Error saving lead:', err);
      setError('There was an issue saving your information. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
      <div className="max-w-xl w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">Your results are ready!</h2>
          <p className="mt-2 text-gray-600">
            Tell us where to send your personalized risk assessment and quote.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg text-sm flex items-start">
            <SafeIcon icon={FiIcons.FiAlertCircle} className="mr-2 mt-0.5" />
            {error}
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">First Name *</label>
              <div className="mt-1">
                <input
                  type="text"
                  name="firstName"
                  id="firstName"
                  required
                  className="shadow-sm focus:ring-brand-green focus:border-brand-green block w-full sm:text-sm border-gray-300 rounded-md p-3 border"
                  value={formData.firstName}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">Last Name *</label>
              <div className="mt-1">
                <input
                  type="text"
                  name="lastName"
                  id="lastName"
                  required
                  className="shadow-sm focus:ring-brand-green focus:border-brand-green block w-full sm:text-sm border-gray-300 rounded-md p-3 border"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address *</label>
              <div className="mt-1">
                <input
                  type="email"
                  name="email"
                  id="email"
                  required
                  className="shadow-sm focus:ring-brand-green focus:border-brand-green block w-full sm:text-sm border-gray-300 rounded-md p-3 border"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone Number *</label>
              <div className="mt-1">
                <input
                  type="tel"
                  name="phone"
                  id="phone"
                  required
                  placeholder="(XXX) XXX-XXXX"
                  className="shadow-sm focus:ring-brand-green focus:border-brand-green block w-full sm:text-sm border-gray-300 rounded-md p-3 border"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">ZIP Code (CA) *</label>
              <div className="mt-1">
                <input
                  type="text"
                  name="zipCode"
                  id="zipCode"
                  required
                  pattern="[0-9]{5}"
                  maxLength="5"
                  className="shadow-sm focus:ring-brand-green focus:border-brand-green block w-full sm:text-sm border-gray-300 rounded-md p-3 border"
                  value={formData.zipCode}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="currentRent" className="block text-sm font-medium text-gray-700">Current Rent (Optional)</label>
              <div className="mt-1">
                <select
                  name="currentRent"
                  id="currentRent"
                  className="shadow-sm focus:ring-brand-green focus:border-brand-green block w-full sm:text-sm border-gray-300 rounded-md p-3 border bg-white"
                  value={formData.currentRent}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {rentAmountOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="bestTime" className="block text-sm font-medium text-gray-700">Best time to call</label>
              <div className="mt-1">
                <select
                  name="bestTime"
                  id="bestTime"
                  className="shadow-sm focus:ring-brand-green focus:border-brand-green block w-full sm:text-sm border-gray-300 rounded-md p-3 border bg-white"
                  value={formData.bestTime}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {bestTimeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="pt-4">
            <Button
              type="submit"
              size="lg"
              className="w-full flex justify-center py-4 text-lg"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Get My Free Quote →'}
            </Button>
            <p className="mt-4 text-xs text-center text-gray-500 flex items-center justify-center">
              <SafeIcon icon={FiIcons.FiShield} className="mr-1" />
              Your information is secure. Get your personalized quote in under 2 minutes - most renters pay less than $15/month!
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}